/**
 * usePhotoRenewal — keeps a signed child-photo URL working without ever
 * refetching the parent list or child form, so in-progress edits elsewhere
 * are never touched.
 *
 * Renewal happens only when actually necessary:
 * - A genuine data refresh (navigation, reload, normal refetch) always
 *   supplies a freshly-signed photoUrl, which this hook picks up immediately.
 * - If a link is ever actually stale when the browser tries to load it
 *   (e.g. an unattended sign-in tablet left open past the link's validity),
 *   the resulting load failure triggers a bounded number of retries by
 *   fetching a fresh signed URL, then exposes `failed` so the caller can
 *   fall back to its existing placeholder.
 */
import { useState, useEffect, useRef, useCallback } from "react";
import axios from "axios";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const MAX_ERROR_RETRIES = 2;

export const usePhotoRenewal = (childId, photoUrl) => {
  const toAbsolute = (url) => (url ? `${BACKEND_URL}${url}` : null);

  const [src, setSrc] = useState(toAbsolute(photoUrl));
  const [failed, setFailed] = useState(false);

  const retryCountRef = useRef(0);
  const srcRef = useRef(src);
  const childIdRef = useRef(childId);
  const inFlightRef = useRef(false);
  const mountedRef = useRef(true);

  srcRef.current = src;
  childIdRef.current = childId;

  // A new photoUrl from a genuine parent refresh (e.g. after upload, or the
  // normal list/detail reload) always wins and resets retry/failure state.
  useEffect(() => {
    setSrc(toAbsolute(photoUrl));
    setFailed(false);
    retryCountRef.current = 0;
  }, [photoUrl, childId]);

  useEffect(() => {
    mountedRef.current = true;
    return () => { mountedRef.current = false; };
  }, []);

  const fetchFreshUrl = useCallback(async (forChildId) => {
    if (!forChildId || inFlightRef.current) return null;
    inFlightRef.current = true;
    try {
      const res = await axios.get(`${API}/children/${forChildId}/photo-url`);
      return res.data?.photoUrl ? toAbsolute(res.data.photoUrl) : null;
    } catch {
      return null;
    } finally {
      inFlightRef.current = false;
    }
  }, []);

  // Bounded-retry recovery — the only renewal path. Only fires when a load
  // actually fails, i.e. renewal happens only when necessary.
  const handleError = useCallback(async () => {
    if (retryCountRef.current >= MAX_ERROR_RETRIES) {
      setFailed(true);
      return;
    }
    retryCountRef.current += 1;
    const requestedFor = childIdRef.current;
    const fresh = await fetchFreshUrl(requestedFor);
    if (!mountedRef.current || childIdRef.current !== requestedFor) return;
    // An unchanged (still-failing) URL is not a successful retry — don't
    // re-set it (React won't re-issue the image request for an identical
    // src, so the retry would silently stall). Count it as a used attempt
    // and let the next error (or exhaustion) proceed.
    if (fresh && fresh !== srcRef.current) {
      setSrc(fresh);
    } else if (retryCountRef.current >= MAX_ERROR_RETRIES) {
      setFailed(true);
    }
  }, [fetchFreshUrl]);

  return { src, failed, handleError };
};
